import { Fancybox, Swiper, A11y, Navigation, Pagination, Thumbs } from 'global-modules';

class ProductMediaSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this?.dataset?.options) {
      this.options = JSON.parse(this.dataset.options);
    }
    this.options.modules = [A11y];

    if (this.options.pagination) {
      this.options.modules.push(Pagination);
    }

    if (this.options.navigation) {
      this.options.modules.push(Navigation);
    }

    this.fancybox();

    window.addEventListener('DOMContentLoaded', () => {
      if (window.windowWidth < 768) {
        this.thumbnailSwiper();
        this.mainSwiper();
      }
    });

    window.addEventListener('window:resize', () => {
      if (window.windowWidth >= 768) {
        this.querySelectorAll('.swiper-slide').forEach(slide => slide.removeAttribute('style'));
        if (this.swiperMainInstance) {
          this.swiperMainInstance.destroy();
        }
      }
      else {
        this.thumbnailSwiper();
        this.mainSwiper();
      }
    });

    // Slide to variant from URL on initial load
    const urlVariantId = parseInt(new URLSearchParams(window.location.search).get('variant'));
    if (urlVariantId) this.slideToVariant({ detail: { variantId: urlVariantId } });

    // Reduce actions (So the event can also be removed)
    this.slideToVariantHandler = event => this.slideToVariant(event);

    // Listen for variant change and go to slide
    document.addEventListener('product:variant-change', this.slideToVariantHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('product:variant-change', this.slideToVariantHandler);
  }

  thumbnailSwiper() {
    if (!Swiper) return false;

    this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
    this.swiperThumbsOptions = {
      a11y: true,
      observer: true,
      spaceBetween: 8,
      slidesPerView: 'auto',
      centerInsufficientSlides: true,
      modules: [A11y, Thumbs],
    };

    this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);

    if (Shopify.designMode && this.swiperThumbsInstance) {
      window.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          this.swiperThumbsInstance.update();
        }, 300);
      });
    }
  }

  mainSwiper() {
    if (!Swiper) return false;

    this.swiperMain = this.querySelector('[data-product-media-swiper]');

    let thumbOptions;
    if (this.swiperThumbsInstance) {
      this.options.modules.push(Thumbs);
      thumbOptions = {
        thumbs: {
          swiper: this.swiperThumbsInstance.hostEl,
          slideThumbActiveClass: 'swiper-slide-thumb-active',
          multipleActiveThumbs: false,
          autoScrollOffset: 1,
        },
      };
    }

    let initialSlideIndex = 0;
    const initialVariantId = parseInt(this.swiperMain.dataset.productMediaSlider);

    if (initialVariantId) {
      const initialSlide = this.matchVariantIdWithSlide(initialVariantId);
      initialSlideIndex = initialSlide ? initialSlide : initialSlideIndex;
    }

    this.swiperMainOptions = {
      a11y: true,
      rewind: true,
      observer: true,
      initialSlide: initialSlideIndex,
      modules: this.options.modules,
      ...this.options,
      ...thumbOptions,
    };

    this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

    if (Shopify.designMode && this.swiperMainInstance) {
      window.addEventListener('shopify:section:load', () => {
        this.swiperMainInstance.update();
      });
    }
  }

  fancybox() {
    if (!Fancybox) return false;

    Fancybox.bind('[data-fancybox="product-media-swiper"]', {
      dragToClose: false,
      showClass: 'f-fadeIn',
      hideClass: 'f-fadeOut',
      mainClass: 'fancybox-products',
      Carousel: {
        transition: 'slide',
      },
      // Disable image zoom animation on opening and closing
      Images: {
        zoom: false,
      },
      Thumbs: {
        type: 'classic',
      },
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
    });
  }

  slideToVariant(event) {
    if (event?.detail?.variantId) {
      const slideIndex = this.matchVariantIdWithSlide(+event.detail.variantId);
      if (typeof slideIndex == 'number') this.goToSlide(slideIndex);
    }
  }

  matchVariantIdWithSlide(id) {
    if (!id || !this.swiperMain) return false;

    const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

    const slide = [...slides].find((slide) => {
      const slideVariants = JSON.parse(slide.dataset.productVariantImage);
      if (slideVariants.includes(id)) return slide;
    });

    if (!slide) return false;

    return this.getNodeIndex(slide);
  }

  goToSlide(slide) {
    if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) return false;

    const index = typeof slide == 'number' ? parseInt(slide) : this.getNodeIndex(slide);

    this.swiperMainInstance.slideTo(index);
  }

  getNodeIndex(node) {
    return Array.from(node.parentNode.children).indexOf(node);
  }
}

if (!customElements.get('product-media-swiper')) {
  customElements.define('product-media-swiper', ProductMediaSlider);
}
